import { Injectable, ErrorHandler, Inject, Optional } from '@angular/core';
import { Logger } from '../common';
import { Subject, Observable } from 'rxjs';

import { REQUEST, RESPONSE } from '@nguniversal/express-engine/tokens';

import * as sourcemappedStacktrace from 'sourcemapped-stacktrace';
import * as express from '../common/express-ssr';
import { environment } from '@tytapp/environments/environment';

@Injectable()
export class AppErrorHandler implements ErrorHandler {
    constructor(
        @Inject(REQUEST) @Optional()
        private request: express.ExpressRequest,
        @Inject(RESPONSE) @Optional()
        private response: express.ExpressResponse
    ) {

    }

    panicked: boolean = false;
    private _networkError: Subject<any> = new Subject<any>();

    public get networkError(): Observable<any> {
        return this._networkError;
    }

    /**
     * Handle an uncaught error.
     * @param error 
     */
    handleError(error) {

        while (error.rejection) {
            console.log("Promise rejection detected. Stripping outer layer.");
            console.groupCollapsed(`Original error data:`);
            console.dir(error);
            console.groupEnd();
            error = error.rejection;
        }

        if (error.errorType == 'network-error') {
            this._networkError.next(error);
            return;
        }

        if (environment.debug) {
            console.error(`(Panic) AppErrorHandler caught an error:`);
            console.error(error);
            return;
        }

        // If we have previously errored, do not error further.
        // This is only really useful in dev mode, but at least for production 
        // we won't go and reload the fancy BG url if that is turned on.

        if (this.panicked)
            return;

        let side = 'client-side';
        if (typeof window === 'undefined') {
            side = 'server-side';

            // If we have request information, include it with the crash.

            if (this.request) {
                Logger.info(`Request which caused this failure: ${this.request.method} ${this.request.url}`);
                Logger.info(`Client IP involved in the request: ${this.request.ip}`);
            }
        }

        if (typeof window !== 'undefined' && error.message && /Loading chunk \d+ failed./.test(error.message)) {
            // Looks like our application version is no longer available. 
            // This should be a rare circumstance. Report the event to the server and attempt to gracefully recover.

            Logger.error(error.message, error);

            // Attempt to reload the page (only if we can guarantee that it will happen exactly once)
            // Hopefully the user will get the new app version with a minor inconvenience

            if (window.localStorage) {
                let allowReload = true;

                if (window.localStorage['appVersionFailureDate']) {
                    let lastDate = parseInt(window.localStorage['appVersionFailureDate']);

                    if (new Date().getTime() - 1000 * 60 < lastDate) {
                        allowReload = false;
                    }
                }

                if (allowReload)
                    window.location.reload();
            }
        }

        this.panicked = true;
        let shownError: Error = error;

        if (error.panic)
            shownError = error.panicError;

        Logger.panic(shownError.message, shownError);

        //this.showPanic(shownError);

        // If the user navigates, force a page refresh. This prevents getting "stuck" in the 
        // panic screen.

        if (typeof window !== 'undefined') {
            window.addEventListener('popstate', () => window.location.reload());
        }

    }

    /**
     * Show the user the panic page
     */
    private showPanic(shownError) {

        let env = environment.name || 'development';
        let enableProductionPanic = true;

        if (!shownError)
            shownError = new Error("No error details are available");

        if (!enableProductionPanic || env != 'production') {
            if (typeof document !== 'undefined') {
                document.getElementById('dev-panic').className = 'visible';

                document.getElementById('dev-panic-message').innerText = shownError.message;
                document.getElementById('dev-panic-backtrace').innerHTML = (shownError.stack || "").replace(/\n/g, "<br/>\n");

                if (shownError.stack) {
                    sourcemappedStacktrace.mapStackTrace(shownError.stack, mappedStack => {
                        document.getElementById('dev-panic-backtrace').innerHTML = mappedStack.join("<br/>\n");
                    });
                }
            }
        } else {
            if (typeof document !== 'undefined') {
                document.getElementById('panic').className = 'visible';
            }
        }

        // Generic adjustments to document

        if (typeof document !== 'undefined') {

            document.documentElement.style.overflowY = 'hidden';
            document.body.style.overflowY = 'hidden';

            // Inject the URL of the YouTube video embed for the panic BG background for production
            let iframe = <HTMLIFrameElement>document.getElementById('panic-bg-video');
            if (iframe)
                iframe.src = 'https://www.youtube.com/embed/3aF_FABP4PU?autoplay=1&loop=1&modestbranding=1&playsinline=1&controls=0&showinfo=0&rel=0';
        }
    }
}